"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import { AppProviders } from "@/components/AppProviders";
import { useAuth } from "@/components/AuthProvider";
import { OrgAccessSplash } from "@/components/OrgAccessSplash";
import { useUrlOrgAccessGate } from "@/hooks/useUrlOrgAccessGate";
import { DashboardShellFallback } from "@/app/DashboardShellFallback";
import { DashboardRouterProvider, useDashboardRouter } from "@/hooks/useDashboardRouter";
import { OrgUrlSyncProvider } from "@/components/OrgUrlSyncProvider";
import { SidebarLayout } from "@/components/SidebarLayout";
import { isCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";

// The Chrome extension iframe (`?embed=extension`) renders the page bare, as
// `_app.tsx` does: no rail, sidebars, command palette, shortcuts modal, or
// the shell's prefetch hooks. The page still needs a Suspense boundary below
// AppProviders: pages that suspend at their root (the PR resolver reads
// useActiveGithubOrgForUser there) would otherwise bubble to the layout
// boundary, discarding the query client and auth tree and re-suspending on
// the fresh cache.
function DashboardShell(t0) {
  const $ = _c(9);
  const {
    children
  } = t0;
  const router = useDashboardRouter();
  const {
    user,
    logout
  } = useAuth();
  const orgAccess = useUrlOrgAccessGate();
  if (orgAccess.state === "pending") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DashboardShellFallback />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (orgAccess.state === "denied") {
    const t1 = user?.username ?? null;
    let t2;
    if ($[1] !== logout || $[2] !== orgAccess.fallbackOrg || $[3] !== orgAccess.org || $[4] !== t1) {
      t2 = <OrgAccessSplash org={orgAccess.org} fallbackOrg={orgAccess.fallbackOrg} username={t1} onLogout={logout} />;
      $[1] = logout;
      $[2] = orgAccess.fallbackOrg;
      $[3] = orgAccess.org;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== children || $[7] !== router.query.embed) {
    t1 = <OrgUrlSyncProvider>{isCodesmithExtensionEmbed(router.query.embed) ? <Suspense fallback={<DashboardShellFallback />}>{children}</Suspense> : <SidebarLayout>{children}</SidebarLayout>}</OrgUrlSyncProvider>;
    $[6] = children;
    $[7] = router.query.embed;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
export default function DashboardLayout(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DashboardShellFallback />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Suspense fallback={t1}><DashboardRouterProvider><AppProviders><DashboardShell>{children}</DashboardShell></AppProviders></DashboardRouterProvider></Suspense>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}